import * as React from 'react';
import { Col, Row } from 'react-bootstrap';
import { connect } from 'react-redux';
import type { ConnectedProps } from 'react-redux';
import { compose } from 'redux';

import type { W_EquipmentUtilization_FindCharts_VM } from 'ab-api/web/equipmentUtilization/findCharts/viewModel';

import { moneyNormalizer, prettyMoney } from '@acceligentllc/shared/utils/formatting';
import { normalizePercentages } from '@acceligentllc/shared/utils/number';

import { ColorPalette } from '@acceligentllc/shared/enums/color';

import { bemBlock, bemElement } from 'af-utils/bem.util';
import { MONEY_FORMATTER } from 'af-utils/chart.util';

import BarMeter from 'af-components/BarMeter';
import Select from 'af-components/Controls/Select';

import * as EquipmentUtilizationActions from 'af-actions/equipmentUtilization';

import type { EquipmentUtilizationTableBaseVM } from './types';
import InfoCardChart from '../../UtilizationShared/InfoCardChart';

const NO_EQUIPMENT_COST_ID = '-1';

interface OwnProps {
	selectedEquipmentCost?: EquipmentUtilizationTableBaseVM;
	equipmentCostOptions: Nullable<EquipmentUtilizationTableBaseVM[]>;
	changeEquipmentCost: (equipmentCostId: string) => void;
}

type Props = OwnProps & ConnectedProps<typeof connector>;

function _getOptionValue(equipmentCost: EquipmentUtilizationTableBaseVM) {
	return equipmentCost.groupId.toString();
}

const InfoCard: React.FC<Props> = (props) => {
	const {
		selectedEquipmentCost = { groupId: NO_EQUIPMENT_COST_ID } as EquipmentUtilizationTableBaseVM,
		equipmentCostOptions = [],
		fetchEquipmentUtilizationCharts,
		changeEquipmentCost,
	} = props;

	const [chartData, setChartData] = React.useState<Nullable<W_EquipmentUtilization_FindCharts_VM>>(null);

	React.useEffect(() => {
		const fetchData = async () => {
			const hasSelectedEquipmentCost = selectedEquipmentCost?.groupId !== NO_EQUIPMENT_COST_ID;
			const hasFetchedChart = !!chartData;

			if (hasSelectedEquipmentCost && (!hasFetchedChart)) {
				const data = await fetchEquipmentUtilizationCharts(+selectedEquipmentCost.groupId);
				setChartData(data);
			}
		};
		fetchData();
	}, [chartData, fetchEquipmentUtilizationCharts, selectedEquipmentCost.groupId]);

	const onEquipmentCostSelect = React.useCallback((equipmentCost: EquipmentUtilizationTableBaseVM) => {
		changeEquipmentCost(equipmentCost.groupId.toString());
	}, [changeEquipmentCost]);

	const renderOptionLabel = React.useCallback((equipmentCost: EquipmentUtilizationTableBaseVM, labelMeta: Metadata) => {
		if (labelMeta.context === 'value') {
			return equipmentCost.name;
		}

		if (selectedEquipmentCost && equipmentCost.name === selectedEquipmentCost.name) {
			return (
				<span className="text-black">
					<strong>{equipmentCost.name}</strong>&nbsp;&nbsp;&nbsp;<span>{equipmentCost.equipmentCount}</span>
				</span>
			);
		}
		return (
			<span>
				{equipmentCost.name}&nbsp;&nbsp;&nbsp;<span className="text-grey">{equipmentCost.equipmentCount}</span>
			</span>
		);
	}, [selectedEquipmentCost]);

	if (!selectedEquipmentCost) {
		throw new Error('No equipment cost selected');
	}

	const { daysAssigned, daysAvailable, daysUnavailable, totalDays, totalRevenue, totalCost, targetProfit } = selectedEquipmentCost;

	const entries = [
		{ label: 'Scheduled', value: daysAssigned, color: ColorPalette.GREEN },
		{ label: 'Not Scheduled', value: daysAvailable, color: ColorPalette.ORANGE },
		{ label: 'Not Available', value: daysUnavailable, color: ColorPalette.RED },
	];

	const [scheduledPercentage, notScheduledPercentage, notAvailablePercentage] = normalizePercentages([
		totalDays ? Math.round(100 * daysAssigned / totalDays) : 0,
		totalDays ? Math.round(100 * daysAvailable / totalDays) : 0,
		totalDays ? Math.round(100 * daysUnavailable / totalDays) : 0,
	]);

	return (
		<div className={`utilization-info-card ${bemBlock('form-box', ['padded-inv'])}`}>
			<Row>
				<Col lg={8} sm={12} xs={24}>
					{selectedEquipmentCost.groupId === NO_EQUIPMENT_COST_ID
						? (
							<div className={bemElement('utilization-info-card', 'equipment-title')}>
								<div className="utilization-info-card__equipment-title__single-value">
									<span className="text-grey">Loading...</span>
								</div>
							</div>
						) : (
							<Select
								className={bemElement('utilization-info-card', 'equipment-title')}
								defaultValue={selectedEquipmentCost}
								formatOptionLabel={renderOptionLabel}
								getOptionValue={_getOptionValue}
								isClearable={false}
								isSearchable={false}
								onValueChange={onEquipmentCostSelect}
								options={equipmentCostOptions ?? []}
							/>
						)
					}
					<BarMeter entries={entries} total={totalDays} />
					<div className={bemElement('utilization-info-card', 'group')}>
						<div className={bemElement('utilization-info-card', 'group-entry', ['padded-right'])}>
							<span className="group-title">Scheduled:</span>
							<span className="group-value text-green">{scheduledPercentage}%</span>
						</div>
						<div className={bemElement('utilization-info-card', 'group-entry', ['padded-right'])}>
							<span className="group-title">Not Scheduled:</span>
							<span className="group-value text-orange">{notScheduledPercentage}%</span>
						</div>
						<div className={bemElement('utilization-info-card', 'group-entry', ['padded-right'])}>
							<span className="group-title">Not Available:</span>
							<span className="group-value text-red">{notAvailablePercentage}%</span>
						</div>
						<div className={bemElement('utilization-info-card', 'group-entry', ['padded-verticaly'])}>
							<span className="group-title">Profit/Loss:</span>
							<span className="group-value">{moneyNormalizer(targetProfit)}</span>
						</div>
					</div>
				</Col>
				<Col className={bemElement('utilization-info-card', 'charts-row')} lg={16} sm={12} xs={24}>
					<div>
						{/** Average maintenance ratio */}
					</div>
					<InfoCardChart
						chartData={chartData?.averageDailyCostChart}
						footerLabel="Total Cost:"
						footerValue={moneyNormalizer(totalCost)}
						hAxisFormatter={MONEY_FORMATTER}
						hAxisLabel="Month"
						title="Average Daily Cost"
						vAxisFormatter={prettyMoney}
					/>
					<InfoCardChart
						chartData={chartData?.averageDailyRevenueChart}
						footerLabel="Total Revenue:"
						footerValue={moneyNormalizer(totalRevenue)}
						hAxisFormatter={MONEY_FORMATTER}
						hAxisLabel="Month"
						title="Average Daily Revenue"
						vAxisFormatter={prettyMoney}
					/>
				</Col>
			</Row>
		</div>
	);
};

function mapDispatchToProps() {
	return {
		fetchEquipmentUtilizationCharts: EquipmentUtilizationActions.fetchEquipmentUtilizationCharts,
	};
}

const connector = connect(null, mapDispatchToProps());

const enhance = compose<React.ComponentType<OwnProps>>(
	React.memo,
	connector
);

export default enhance(InfoCard);
